<script setup lang="ts">
import type { Component } from 'vue';

import { Info } from 'lucide-vue-next';

defineProps<{
  open: boolean;
  variant: 'dialog' | 'sheet' | 'full-screen-sheet';
  title: string;
  icon?: Component;
  closeLabel?: string;
  backLabel?: string;
  dialogContentClass?: string;
  sheetContentClass?: string;
  hideClose?: boolean;
}>();
defineEmits<{ 'update:open': [value: boolean] }>();
</script>

<template>
  <UiCustomBottomSheet
    v-if="variant === 'sheet'"
    :open="open"
    :title="title"
    :close-label="closeLabel ?? $t('components.common.close')"
    :content-class="sheetContentClass"
    :hide-close="hideClose"
    @update:open="(value: boolean) => $emit('update:open', value)"
  >
    <slot />
    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
  </UiCustomBottomSheet>

  <UiCustomFullScreenSheet
    v-else-if="variant === 'full-screen-sheet'"
    :open="open"
    :title="title"
    :back-label="backLabel ?? $t('components.common.back')"
    :content-class="sheetContentClass"
    @update:open="(value: boolean) => $emit('update:open', value)"
  >
    <slot />
  </UiCustomFullScreenSheet>

  <UiDialog
    v-else
    :open="open"
    @update:open="(value: boolean) => $emit('update:open', value)"
  >
    <UiDialogContent
      :class="cn('max-w-lg p-8', dialogContentClass)"
      :hide-close="hideClose"
    >
      <div class="flex flex-col gap-3">
        <UiDialogHeader :class="{ 'pr-8': !hideClose }">
          <UiDialogTitle class="flex items-center gap-4 text-left text-xl font-semibold lg:text-2xl">
            <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary-50">
              <component
                :is="icon ?? Info"
                class="h-5 w-5 text-brand-primary-700"
                aria-hidden="true"
              />
            </span>
            {{ title }}
          </UiDialogTitle>
          <UiDialogDescription class="sr-only">{{ title }}</UiDialogDescription>
        </UiDialogHeader>

        <slot />
        <slot name="footer" />
      </div>
    </UiDialogContent>
  </UiDialog>
</template>
